/**
 * "Files to send" on the Submit page (R3 T6a/T6b, plan §1.1 and §3.7).
 *
 * Each checklist item shows the ONE file it will send — a kept Word or PDF
 * version, a completed buyer form, an Excel schedule export or an uploaded
 * file — with its checks. "Download files" zips exactly those files.
 * After Mark lodged the frozen copies are listed above, read-only.
 *
 * A component, not a route: it mounts in R4's SubmitSection and works in any
 * shell that gives it a bid id. The Pack below it keeps its own "Export draft
 * pack", built from the current text.
 */

import { useCallback, useEffect, useRef } from 'react'
import { Download, Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { LodgedRecords } from '@/components/pack/LodgedRecords'
import { SubmissionFileRow } from '@/components/pack/SubmissionFileRow'
import { downloadFromUrl, toastApiError } from '@/lib/api'
import { useSubmissionFilesStore } from '@/stores/submission-files-store'
import { useSubmissionPackStore } from '@/stores/submission-pack-store'
import type { SubmissionRecordFile } from '@/types/submission-files'

interface SubmissionFilesSectionProps {
  bidId: string
  readOnly: boolean
  /** The bid was lodged but has moved back to Active or Lead: lodged records are history. */
  lodgedIsHistory?: boolean
}

export function SubmissionFilesSection({ bidId, readOnly, lodgedIsHistory = false }: SubmissionFilesSectionProps) {
  const data = useSubmissionFilesStore((s) => (s.bidId === bidId ? s.data : null))
  const isLoading = useSubmissionFilesStore((s) => s.isLoading)
  const error = useSubmissionFilesStore((s) => (s.bidId === bidId ? s.error : null))
  const isDownloading = useSubmissionFilesStore((s) => s.isDownloading)
  const load = useSubmissionFilesStore((s) => s.load)
  const refresh = useSubmissionFilesStore((s) => s.refresh)
  const pin = useSubmissionFilesStore((s) => s.pin)
  const downloadAll = useSubmissionFilesStore((s) => s.downloadAll)
  const ensureFresh = useSubmissionFilesStore((s) => s.ensureFresh)
  // Linking, unlinking or uploading on the Pack below changes which file an
  // item sends: the Pack re-reads after every write, so follow it.
  const pack = useSubmissionPackStore((s) => (s.bidId === bidId ? s.pack : null))

  useEffect(() => {
    void load(bidId)
  }, [bidId, load])

  // The first pack read only races the load above; refresh on the ones after it.
  const seenPack = useRef(false)
  useEffect(() => {
    if (!pack) return
    if (!seenPack.current) {
      seenPack.current = true
      return
    }
    void refresh()
  }, [pack, refresh])

  const handlePin = useCallback(
    (itemId: string, outputVersionId: string | null) => {
      pin(itemId, outputVersionId).catch((err: unknown) => toastApiError(err, "Couldn't change the file"))
    },
    [pin]
  )

  const handleDownload = useCallback(
    async (itemId: string) => {
      const fresh = await ensureFresh()
      const file = fresh?.items.find((i) => i.itemId === itemId)?.file
      if (file) downloadFromUrl(file.downloadUrl, file.name)
      else toast.error('That file is no longer available')
    },
    [ensureFresh]
  )

  const handleRecordDownload = useCallback(
    async (target: SubmissionRecordFile) => {
      const fresh = await ensureFresh()
      const file = fresh?.records.flatMap((r) => [...r.files, ...(r.receipt ? [r.receipt] : [])]).find((f) => f.id === target.id)
      downloadFromUrl((file ?? target).downloadUrl, target.name)
    },
    [ensureFresh]
  )

  const handleDownloadAll = useCallback(() => {
    downloadAll().catch((err: unknown) => toastApiError(err, "Couldn't download the files"))
  }, [downloadAll])

  if (!data) {
    if (error) {
      return (
        <section aria-label="Files to send" className="space-y-2">
          <h2 className="text-sm font-medium">Files to send</h2>
          <p className="text-meta">{error}</p>
          <Button variant="outline" size="sm" onClick={() => void load(bidId)}>
            Try again
          </Button>
        </section>
      )
    }
    return isLoading ? (
      <section aria-label="Files to send" className="flex items-center gap-2 text-meta">
        <Loader2 aria-hidden className="h-4 w-4 animate-spin" />
        Loading files…
      </section>
    ) : null
  }

  const withFile = data.items.filter((item) => item.file).length

  return (
    <section aria-label="Files to send" className="space-y-3">
      <LodgedRecords
        records={data.records}
        isHistory={lodgedIsHistory}
        onDownload={(file) => void handleRecordDownload(file)}
      />

      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-sm font-medium">Files to send</h2>
          <p className="text-meta">
            {data.items.length === 0
              ? 'No checklist item needs a file yet.'
              : `${withFile} of ${data.items.length} ${data.items.length === 1 ? 'item has' : 'items have'} a file`}
          </p>
        </div>
        {withFile > 0 && (
          <Button variant="ghost" size="sm" onClick={handleDownloadAll} disabled={isDownloading}>
            {isDownloading ? (
              <Loader2 aria-hidden className="mr-1 h-4 w-4 animate-spin" />
            ) : (
              <Download aria-hidden className="mr-1 h-4 w-4" />
            )}
            {isDownloading ? 'Preparing…' : 'Download files'}
          </Button>
        )}
      </div>

      {data.items.length > 0 && (
        <ul className="divide-y divide-border">
          {data.items.map((item) => (
            <SubmissionFileRow
              key={item.itemId}
              item={item}
              readOnly={readOnly}
              onPin={(outputVersionId) => handlePin(item.itemId, outputVersionId)}
              onDownload={() => void handleDownload(item.itemId)}
            />
          ))}
        </ul>
      )}
    </section>
  )
}
